import type { GearCategory, MonitorPreference, Profile, Rider, RiderMember, StageInput } from '@tunasong/schemas'
import { normalizeParts, stageInputStandLabels } from '@tunasong/schemas'
import { applyOrder, channelKey, hasCustomOrder } from './channel-order.js'
import { stageBucket } from './role-label.js'

/**
 * View model for the rider's venue layout (docs/ux/2026-10-16-rider-venue-layout-ux.md).
 *
 * Everything the venue reads is derived here so the view only renders. Sections or tiles without data (advance contact,
 * or inputs on a rider synced before members added them) are absent, never zero. Members' Phase 1 inputs give one row
 * per channel; a member without inputs keeps the legacy one-row-per-member shape.
 */

/** Gear categories plus DI boxes, which only inputs produce */
export type VenueLineCategory = GearCategory | 'di'

export interface VenueFor {
  /** An input source ("Snare top"), or a member name for items listed as gear */
  label: string
  /** How many times the same label (case-insensitive) contributed */
  count: number
}

export interface VenueLine {
  /** Merge key: category + trimmed, case-insensitive name */
  key: string
  category: VenueLineCategory
  /** First-seen spelling, trimmed */
  name: string
  quantity: number
  /** What the item is for: input sources, or member names for gear */
  forLabels: VenueFor[]
  /** Distinct notes, never dropped */
  notes: string[]
}

export const venueGroupIds = ['microphones', 'dis', 'stands', 'monitors', 'backline', 'other'] as const
export type VenueGroupId = (typeof venueGroupIds)[number]

export interface VenueGroup {
  id: VenueGroupId
  title: string
  lines: VenueLine[]
}

export type VenueTileId = 'inputs' | 'monitorMixes' | 'youProvide' | 'stage'

export interface VenueTile {
  id: VenueTileId
  label: string
  value: string
  detail?: string
}

export interface VenueInputRow {
  channel?: number
  /** The input's source; for a member without inputs, their role (or '—') */
  source: string
  /** The member's display name */
  player: string
  micDi: string[]
  stands: string[]
  /** Needs phantom power (48V) */
  phantom: boolean
  /** False only for a member without inputs who lists nothing */
  hasRequirements: boolean
}

export interface VenueMonitorRow {
  mix: number
  who: string[]
  /** Wedge / In-ear, from the members' snapshotted monitoring; absent when unknown */
  type?: string
}

export interface VenueNote {
  who: string
  note: string
}

export interface VenueStage {
  minimumStage?: string
  power?: string
  pa?: string
}

/** Who the venue calls with questions. The lead typed it into this rider, so publishing it is intended. */
export interface VenueContact {
  name?: string
  phone?: string
  email?: string
}

export interface VenueSheet {
  bandName: string
  venue?: string
  /** Event · venue · formatted date, missing parts omitted */
  eventLine?: string
  /** Formatted `updatedAt` */
  version?: string
  /** The advance contact, without the user id; absent when the rider has none */
  contact?: VenueContact
  isEmpty: boolean
  tiles: VenueTile[]
  venueProvides: VenueGroup[]
  bandBrings: VenueGroup[]
  inputs: VenueInputRow[]
  /** Whether any member has Phase 1 inputs: the list is per channel and has a 48V column */
  hasInputs: boolean
  monitors: VenueMonitorRow[]
  stage?: VenueStage
  /** Each member's note for the engineer, attributed */
  notes: VenueNote[]
  /** Names of members with no stage setup */
  missingSetup: string[]
  /** In-app readiness warning naming every member without a stage setup, one line */
  readinessWarning?: string
  /** Whether a hospitality rider exists; the footer only says it is sent separately when it does */
  hasHospitality: boolean
}

/** The only profile fields the venue sheet reads */
export type VenueProfile = Pick<Profile, 'userId' | 'name' | 'email'>

export interface BuildVenueSheetOptions {
  /** The band's name, e.g. the parent bandspace. Falls back to the rider name. */
  bandName?: string
  /** Locale for dates. Defaults to the viewer's locale. */
  locale?: string
}

/** Groups in display order */
const groupDefinitions: { id: VenueGroupId; title: string; categories: VenueLineCategory[] }[] = [
  { id: 'microphones', title: 'Microphones', categories: ['microphone'] },
  { id: 'dis', title: 'DI boxes', categories: ['di'] },
  { id: 'stands', title: 'Stands', categories: ['stand'] },
  { id: 'monitors', title: 'Monitors', categories: ['monitor'] },
  { id: 'backline', title: 'Backline', categories: ['instrument', 'backline'] },
  { id: 'other', title: 'Other', categories: ['accessory', 'other'] },
]

const wedgeName = 'Wedge'

const trimmed = (value?: string) => {
  const t = value?.trim()
  return t ? t : undefined
}

const positiveQuantity = (quantity: number) => (Number.isFinite(quantity) && quantity > 0 ? quantity : 1)

/** The text before `@`, never the full address: a venue document never shows a user id or an email address. */
const emailLocalPart = (email?: string) => trimmed(email?.split('@')[0])

const lineKey = (category: VenueLineCategory, name: string) => `${category}:${name.trim().toLowerCase()}`

const pushDistinct = (list: string[], value: string) => {
  if (!list.some(v => v.toLowerCase() === value.toLowerCase())) {
    list.push(value)
  }
}

interface Contribution {
  category: VenueLineCategory
  name: string
  quantity: number
  notes?: string
  forLabel?: string
}

const countFor = (list: VenueFor[], label: string) => {
  const existing = list.find(f => f.label.toLowerCase() === label.toLowerCase())
  if (existing) {
    existing.count += 1
    return
  }
  list.push({ label, count: 1 })
}

/** What a venue has to supply for one input: its mic or DI box, and its stand. `Line` needs neither. */
const inputContributions = (input: StageInput): Contribution[] => {
  const forLabel = input.source.trim()
  const contributions: Contribution[] = []
  const micDi = trimmed(input.micDi)
  if (micDi && !/^line$/i.test(micDi)) {
    const isDi = /\bDI\b/i.test(micDi)
    contributions.push({
      category: isDi ? 'di' : 'microphone',
      name: isDi && /^DI$/i.test(micDi) ? 'DI box' : micDi,
      quantity: 1,
      notes: input.notes,
      forLabel,
    })
  }
  if (input.stand && input.stand !== 'none') {
    const name = input.stand === 'clip' ? 'Mic clip' : `${stageInputStandLabels[input.stand]} stand`
    contributions.push({ category: 'stand', name, quantity: 1, forLabel })
  }
  return contributions
}

/**
 * A quantity that describes the same items as member-listed lines, rather than adding to them. The line takes the
 * larger of the two, keeping the members' names and notes.
 */
interface QuantityFloor {
  category: VenueLineCategory
  name: string
  quantity: number
}

const aggregate = (contributions: Contribution[], floors: QuantityFloor[] = []): VenueGroup[] => {
  const lines = new Map<string, VenueLine>()
  for (const contribution of contributions) {
    const name = trimmed(contribution.name)
    if (!name) {
      continue
    }
    const key = lineKey(contribution.category, name)
    const line = lines.get(key) ?? {
      key,
      category: contribution.category,
      name,
      quantity: 0,
      forLabels: [],
      notes: [],
    }
    line.quantity += positiveQuantity(contribution.quantity)
    if (contribution.forLabel) {
      countFor(line.forLabels, contribution.forLabel)
    }
    const note = trimmed(contribution.notes)
    if (note) {
      pushDistinct(line.notes, note)
    }
    lines.set(key, line)
  }
  for (const floor of floors) {
    const key = lineKey(floor.category, floor.name)
    const line = lines.get(key)
    if (line) {
      line.quantity = Math.max(line.quantity, floor.quantity)
    } else {
      lines.set(key, {
        key,
        category: floor.category,
        name: floor.name,
        quantity: floor.quantity,
        forLabels: [],
        notes: [],
      })
    }
  }

  const all = [...lines.values()]
  return groupDefinitions
    .map(({ id, title, categories }) => ({
      id,
      title,
      // Category order within a group follows the definition, then first-seen order
      lines: categories.flatMap(category => all.filter(line => line.category === category)),
    }))
    .filter(group => group.lines.length > 0)
}

const parseLocalDate = (value: string) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]))
  }
  return new Date(value)
}

/** Long, readable event date. `YYYY-MM-DD` is read as a local date so the day never shifts. */
export const formatEventDate = (value: string, locale?: string) => {
  const date = parseLocalDate(value.trim())
  if (Number.isNaN(date.getTime())) {
    return value.trim()
  }
  return new Intl.DateTimeFormat(locale, { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }).format(
    date
  )
}

/** Short date for the version stamp, e.g. "16 Oct 2026". */
export const formatVersionDate = (value: string, locale?: string) => {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) {
    return undefined
  }
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'short', year: 'numeric' }).format(date)
}

/** Edit stores a missing dimension as 0, so only positive dimensions count. */
export const formatStageSize = (size?: { width: number; depth: number }) => {
  const width = size && size.width > 0 ? size.width : undefined
  const depth = size && size.depth > 0 ? size.depth : undefined
  if (width && depth) {
    return `${width} × ${depth} m`
  }
  if (width) {
    return `${width} m wide`
  }
  if (depth) {
    return `${depth} m deep`
  }
  return undefined
}

/** Quote untrusted text for a CSS string (the print footer's `@page` content). */
export const toCssString = (value: string) =>
  `"${value
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/</g, '\\3C ')
    .replace(/[\n\r\f]+/g, ' ')}"`

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`

export const monitorTypeLabels: Record<MonitorPreference, string> = {
  wedge: 'Wedge',
  iem: 'In-ear',
  both: 'Wedge + in-ear',
  none: 'None',
}

/** `{ contact }` with the contact's non-blank parts, or nothing when every part is blank */
const venueContact = (contact: Rider['advanceContact']): { contact?: VenueContact } => {
  const parts = Object.entries({
    name: trimmed(contact?.name),
    phone: trimmed(contact?.phone),
    email: trimmed(contact?.email),
  }).filter((entry): entry is [keyof VenueContact, string] => entry[1] !== undefined)
  return parts.length > 0 ? { contact: Object.fromEntries(parts) } : {}
}

const memberInputs = (m: RiderMember) => (m.inputs ?? []).filter(input => input.source.trim())

/** A member's stage-earliest part, for inputs without a part and for members without inputs */
const memberBucket = (m: RiderMember) =>
  Math.min(...(normalizeParts(m.parts) ?? []).map(stageBucket), stageBucket(undefined))

interface PlacedRow {
  /** The channel key (`channel-order.ts`). Holds a user id, so it never reaches the sheet. */
  key: string
  row: VenueInputRow
  /** One of the member's inputs, as opposed to the legacy one-row-per-member shape */
  isInput: boolean
  bucket: number
  channelNumber: number
  memberIndex: number
  inputIndex: number
}

/** One input-list row with its channel key, for the Edit layout's channel order list */
export interface OrderedChannel {
  key: string
  row: VenueInputRow
}

/**
 * Where a member's display name comes from: a profile, Edit's already-resolved names, or the
 * venue link's public profile projection (which has no email, so the email fallback never fires).
 */
export type VenueNameSource = Pick<VenueProfile, 'userId'> & Partial<Pick<VenueProfile, 'name' | 'email'>>

const memberNames = (members: RiderMember[], profiles: VenueNameSource[]) => {
  const profileNames = new Map(profiles.map(p => [p.userId, trimmed(p.name) ?? emailLocalPart(p.email)]))
  return (m: RiderMember) =>
    profileNames.get(m.userId) ??
    (m.channelNumber === undefined ? `Member ${members.indexOf(m) + 1}` : `Member ${m.channelNumber}`)
}

/*
 * Input list. Once any member has inputs: one row per input in stage order (drums first, vocals last), numbered
 * 1..n with no gaps; members without inputs keep one unnumbered row. A rider without inputs keeps the legacy shape:
 * one row per member in channel order, numbered by the member's channel. The lead's `inputOrder` then reorders the
 * rows (new ones after the ordered ones) and the numbers re-flow 1..n, member rows included in a legacy rider.
 */
export const orderedChannels = (rider: Rider, profiles: VenueNameSource[]): OrderedChannel[] => {
  const members = rider.members ?? []
  const memberName = memberNames(members, profiles)
  const hasInputs = members.some(m => memberInputs(m).length > 0)
  const placed = members.flatMap((m, memberIndex): PlacedRow[] => {
    const channelNumber = m.channelNumber ?? Number.POSITIVE_INFINITY
    const fallbackBucket = memberBucket(m)
    const player = memberName(m)
    const rowInputs = memberInputs(m)
    if (rowInputs.length > 0) {
      return rowInputs.map((input, inputIndex) => {
        const micDi = trimmed(input.micDi)
        const stand = input.stand && input.stand !== 'none' ? stageInputStandLabels[input.stand] : undefined
        return {
          key: channelKey(m, input),
          row: {
            source: input.source.trim(),
            player,
            micDi: micDi ? [micDi] : [],
            stands: stand ? [stand] : [],
            phantom: Boolean(input.phantomPower),
            hasRequirements: true,
          },
          isInput: true,
          bucket: input.part ? stageBucket(input.part) : fallbackBucket,
          channelNumber,
          memberIndex,
          inputIndex,
        }
      })
    }
    const requirements = (m.requirements ?? []).filter(r => trimmed(r.name))
    const names = (category: GearCategory) => requirements.filter(r => r.category === category).map(r => r.name.trim())
    return [
      {
        key: channelKey(m),
        row: {
          ...(hasInputs || m.channelNumber === undefined ? {} : { channel: m.channelNumber }),
          source: trimmed(m.role) ?? '—',
          player,
          micDi: names('microphone'),
          stands: names('stand'),
          phantom: false,
          hasRequirements: requirements.length > 0,
        },
        isInput: false,
        bucket: hasInputs ? fallbackBucket : 0,
        channelNumber,
        memberIndex,
        inputIndex: 0,
      },
    ]
  })
  const sorted = [...placed].sort(
    (a, b) =>
      a.bucket - b.bucket ||
      (a.channelNumber === b.channelNumber ? 0 : a.channelNumber < b.channelNumber ? -1 : 1) ||
      a.memberIndex - b.memberIndex ||
      a.inputIndex - b.inputIndex
  )
  const custom = hasCustomOrder(sorted, rider.inputOrder)
  const ordered = custom ? applyOrder(sorted, rider.inputOrder) : sorted
  let channel = 0
  return ordered.map(({ key, row, isInput }) => {
    // A legacy rider's member rows keep their stored channel until the lead orders them
    if (!isInput && (hasInputs || !custom)) {
      return { key, row }
    }
    channel += 1
    return { key, row: { ...row, channel } }
  })
}

export const buildVenueSheet = (
  rider: Rider & { updatedAt?: string },
  profiles: VenueNameSource[],
  options: BuildVenueSheetOptions = {}
): VenueSheet => {
  const { locale } = options
  const members = rider.members ?? []
  const shared = rider.shared
  const memberName = memberNames(members, profiles)
  const memberLabel = (m: RiderMember) => {
    const role = trimmed(m.role)
    return role ? `${memberName(m)} (${role})` : memberName(m)
  }

  // Venue provides / Band brings
  const venueContributions: Contribution[] = []
  const bandContributions: Contribution[] = []
  for (const m of members) {
    for (const requirement of m.requirements ?? []) {
      const target = requirement.providedByArtist ? bandContributions : venueContributions
      target.push({ ...requirement, forLabel: memberName(m) })
    }
    for (const input of memberInputs(m)) {
      const target = input.providedByArtist ? bandContributions : venueContributions
      target.push(...inputContributions(input))
    }
  }
  for (const requirement of shared?.backline ?? []) {
    const target = requirement.providedByArtist ? bandContributions : venueContributions
    target.push(requirement)
  }
  // shared.monitors.wedges and member-listed wedges describe the same wedges: take the larger, never the sum
  const wedges = shared?.monitors?.wedges ?? 0
  const wedgeFloors: QuantityFloor[] = wedges > 0 ? [{ category: 'monitor', name: wedgeName, quantity: wedges }] : []
  const venueProvides = aggregate(venueContributions, wedgeFloors)
  const bandBrings = aggregate(bandContributions)

  const hasInputs = members.some(m => memberInputs(m).length > 0)
  const inputs: VenueInputRow[] = orderedChannels(rider, profiles).map(({ row }) => row)
  const inputCount = members.reduce((sum, m) => sum + memberInputs(m).length, 0)
  const phantomCount = inputs.filter(row => row.phantom).length

  const missingSetup = members
    .filter(m => !(m.requirements ?? []).some(r => trimmed(r.name)) && memberInputs(m).length === 0)
    .map(memberName)
  const readinessWarning =
    missingSetup.length === 0
      ? undefined
      : missingSetup.length === 1
        ? `${missingSetup[0]} hasn't added a stage setup. The venue will see "no requirements listed".`
        : `${new Intl.ListFormat(locale, { style: 'long', type: 'conjunction' }).format(missingSetup)} haven't added a stage setup. The venue will see "no requirements listed" for them.`

  // Monitors: grouped by mix number, with the distinct monitor types of the members on the mix
  const mixes = new Map<number, { who: string[]; types: string[] }>()
  for (const m of members) {
    if (m.monitorMix === undefined) {
      continue
    }
    const mix = mixes.get(m.monitorMix) ?? { who: [], types: [] }
    mix.who.push(memberLabel(m))
    if (m.monitorPreference) {
      pushDistinct(mix.types, monitorTypeLabels[m.monitorPreference])
    }
    mixes.set(m.monitorMix, mix)
  }
  const monitors: VenueMonitorRow[] = [...mixes.entries()]
    .sort(([a], [b]) => a - b)
    .map(([mix, { who, types }]) => (types.length > 0 ? { mix, who, type: types.join(', ') } : { mix, who }))

  const notes: VenueNote[] = members.flatMap(m => {
    const note = trimmed(m.engineerNotes)
    return note ? [{ who: memberName(m), note }] : []
  })

  // Tiles: omitted without data, never shown as 0
  const tiles: VenueTile[] = []
  if (inputCount > 0) {
    tiles.push({
      id: 'inputs',
      label: 'Inputs',
      value: `${inputCount}`,
      ...(phantomCount > 0 ? { detail: `${phantomCount} need 48V` } : {}),
    })
  }
  const mixCount = shared?.monitors?.mixes && shared.monitors.mixes > 0 ? shared.monitors.mixes : mixes.size
  if (mixCount > 0) {
    const iems = shared?.monitors?.iemPacks ?? 0
    const detail = [wedges > 0 ? plural(wedges, 'wedge', 'wedges') : undefined, iems > 0 ? `${iems} in-ear` : undefined]
      .filter(Boolean)
      .join(', ')
    tiles.push({ id: 'monitorMixes', label: 'Monitor mixes', value: `${mixCount}`, ...(detail ? { detail } : {}) })
  }
  const venueItemCount = venueProvides.reduce((sum, g) => sum + g.lines.reduce((s, l) => s + l.quantity, 0), 0)
  if (venueItemCount > 0) {
    tiles.push({
      id: 'youProvide',
      label: 'You provide',
      value: `${venueItemCount}`,
      detail: venueItemCount === 1 ? 'item' : 'items',
    })
  }
  const minimumStage = formatStageSize(shared?.stageSize)
  if (minimumStage) {
    tiles.push({ id: 'stage', label: 'Stage', value: minimumStage, detail: 'minimum' })
  }

  // Stage and power
  const power = trimmed(shared?.power)
  const pa = trimmed(shared?.paSystem)
  const stage = minimumStage || power || pa ? { minimumStage, power, pa } : undefined

  const hospitality = rider.hospitality
  const hasHospitality = Boolean(
    hospitality &&
    ((hospitality.meals ?? 0) > 0 ||
      (hospitality.parkingSpaces ?? 0) > 0 ||
      (hospitality.drinks ?? []).some(d => trimmed(d)) ||
      trimmed(hospitality.notes))
  )

  // Header
  const venue = trimmed(rider.venue)
  const eventDate = trimmed(rider.eventDate)
  const eventParts = [
    trimmed(rider.eventName),
    venue,
    eventDate ? formatEventDate(eventDate, locale) : undefined,
  ].filter(Boolean)

  return {
    bandName: trimmed(options.bandName) ?? trimmed(rider.name) ?? 'Technical rider',
    venue,
    eventLine: eventParts.length > 0 ? eventParts.join(' · ') : undefined,
    version: rider.updatedAt ? formatVersionDate(rider.updatedAt, locale) : undefined,
    ...venueContact(rider.advanceContact),
    isEmpty:
      members.length === 0 &&
      venueProvides.length === 0 &&
      bandBrings.length === 0 &&
      monitors.length === 0 &&
      notes.length === 0 &&
      !stage,
    tiles,
    venueProvides,
    bandBrings,
    inputs,
    hasInputs,
    monitors,
    stage,
    notes,
    missingSetup,
    readinessWarning,
    hasHospitality,
  }
}
